import React, { forwardRef, ReactNode } from 'react'
import cx from 'classnames'
import { Menu, MenuButton, MenuItems } from '@headlessui/react'

import { Text } from 'components/dataDisplay'

import Item, { ItemProps } from './components/Item/Item'

import * as s from './DropdownMenu.scss'
import { useDevice } from 'device'
import { checkWebView } from 'helpers'


export type DropdownMenuProps = {
  className?: string
  itemsClassName?: string
  buttonClassName?: string
  children?: React.ReactElement<{ isOpen: boolean }>
  items?: Omit<ItemProps, 'style'>[]
  itemsNode?: ReactNode
  sections?: Header.NavigationItem['sections']
  style?: ItemProps['style']
  placement?: 'bottomLeft' | 'bottomRight' | 'left' | 'right'
  disabled?: boolean
  onOpen?: (isOpen: boolean) => void
}

const DropdownMenu: React.FC<DropdownMenuProps> = (props) => {
  const { children, className, itemsClassName, buttonClassName, items, itemsNode, sections, placement = 'left', style, disabled, onOpen } = props

  const { isAndroid } = useDevice()
  const { isWebView } = checkWebView(navigator.userAgent)

  if (!React.isValidElement(children)) {
    throw new Error('DropdownMenu children should be a single valid element')
  }

  const rootClassName = cx('relative', className, { 'opacity-32': disabled })
  const menuClassName = cx('block py-12px bg-white radius-8 shadow-titanic-1', s.menu, itemsClassName, {
    [s[placement]]: placement,
  })

  const ItemsNode = forwardRef((props, ref) => {
    if (sections) {
      return (
        <div className="flex">
          {
            sections.map(({ title: sectionTitle, id: sectionId }, index) => {
              const sectionItems = items.filter(((item) => item?.sectionId === sectionId))

              return Boolean(sectionItems?.length) && (
                <div className={cx({ 'ml-40px': index })} key={sectionId}>
                  <Text
                    className="mt-24px ml-20px mb-12px opacity-48"
                    message={sectionTitle}
                    size="s13-r"
                  />
                  {
                    sectionItems.map((item, index) => {
                      return Boolean(item) && (
                        <Item
                          className="select-none"
                          key={index}
                          style={style}
                          {...item}
                          ref={ref}
                          {...props}
                        />
                      )
                    })
                  }
                </div>
              )
            })
          }
        </div>
      )
    }
    else if (items) {
      return items.map((itemProps, index) => (
        <Item
          className="select-none"
          key={index}
          style={style}
          {...itemProps}
          ref={ref}
          {...props}
        />
      ))
    }
    else {
      return itemsNode
    }
  })

  return (
    <Menu as="div" className={rootClassName}>
      <MenuButton
        className={cx(buttonClassName, 'flex items-center min-w-0', { 'pointer': !disabled })}
        aria-label="Menu"
        disabled={disabled}
      >
        {
          ({ open }) => {
            if (typeof onOpen === 'function') {
              onOpen(open)
            }

            return (
              <div
                onClick={(event) => {
                  if (isAndroid || isWebView) return // fix menu on ProfileButton click is not opening on Android UA

                  event.preventDefault()
                  event.stopPropagation()
                }}
              >
                {
                  React.cloneElement(children, { isOpen: open })
                }
              </div>
            )
          }
        }
      </MenuButton>
      <MenuItems
        className={menuClassName}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}>
        <ItemsNode />
      </MenuItems>
    </Menu>
  )
}


export default React.memo(DropdownMenu)
